import { StrictMode } from "react";
import React, { Component, ReactNode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import Home from "./screens/Home/Home";
import Home3D from "./screens/Home3D/Home3D";
import { HomeWheel } from "./screens/HomeWheel";
import { Profile } from "./screens/Profile/Profile";
import { Resume } from "./screens/Resume";
import { InteractiveHeroPage } from "./screens/InteractiveHero";
import { VolvoConfigurator, MackTrucks, ZeekrFlexible, VolvoTrucksARFlexible, Formula1Flexible, NikeFlexible, NissanDxFlexible, VolvoTrucksAeroFlexible, NiveaFlexible, DieterRamsFlexible, AIPlaygroundFlexible, VolvoTrucksNAFlexible, VolvoRiderFlexible, PolestarEngineeredFlexible, DritaImeFlexible, WordPlayFlexible } from "./screens/CaseStudy/CaseStudy";
import { ThemeProvider } from "./context/ThemeContext";
import { ViewToggle } from "./components/ViewToggle";
import { ScrollToTop } from "./components/ScrollToTop";
import "./styles/globals.css";
import "./styles/fonts.css";
import "./styles/scrollbar.css";
import "./styles/3d-homepage.css";
import "./styles/video-protection.css";
import { inject } from '@vercel/analytics';
import { SpeedInsights } from '@vercel/speed-insights/react';

interface ErrorBoundaryState {
  hasError: boolean;
}

interface ErrorBoundaryProps {
  children: ReactNode;
}

// Error boundary component for catching runtime errors
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_error: Error): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error('React Error Boundary caught an error:', error, errorInfo);
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-white dark:bg-black">
          <div className="text-center p-8">
            <h1 className="text-2xl font-bold mb-4 text-black dark:text-white">Something went wrong</h1>
            <button 
              onClick={() => window.location.reload()} 
              className="px-4 py-2 bg-black dark:bg-white text-white dark:text-black rounded hover:opacity-80"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Function to initialize the app
function initializeApp(): HTMLElement {
  const rootElement = document.getElementById("root");
  
  if (!rootElement) {
    console.error("Root element not found. Creating fallback container.");
    const fallbackRoot = document.createElement("div");
    fallbackRoot.id = "root";
    document.body.appendChild(fallbackRoot);
    return fallbackRoot;
  }
  
  return rootElement;
}

// Create root and render app
const root = createRoot(initializeApp());

root.render(
  <StrictMode>
    <ErrorBoundary>
      <HelmetProvider>
        <ThemeProvider>
          <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <ScrollToTop />
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/3d" element={<Home3D />} />
              <Route path="/wheel" element={<HomeWheel />} />
              <Route path="/work" element={<Home />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/resume" element={<Resume />} />
              <Route path="/interactive-hero" element={<InteractiveHeroPage />} />
              <Route path="/volvo-configurator" element={<VolvoConfigurator />} />
              <Route path="/mack-trucks" element={<MackTrucks />} />
              <Route path="/zeekr" element={<ZeekrFlexible />} />
              <Route path="/volvo-trucks-ar" element={<VolvoTrucksARFlexible />} />
              <Route path="/formula1" element={<Formula1Flexible />} />
        <Route path="/nike" element={<NikeFlexible />} />
        <Route path="/nissan-idx" element={<NissanDxFlexible />} />
        <Route path="/volvo-trucks-aero" element={<VolvoTrucksAeroFlexible />} />
        <Route path="/nivea" element={<NiveaFlexible />} />
              <Route path="/dieter-rams" element={<DieterRamsFlexible />} />
              <Route path="/drita-ime" element={<DritaImeFlexible />} />
              <Route path="/ai-playground" element={<AIPlaygroundFlexible />} />
              <Route path="/volvo-trucks-na" element={<VolvoTrucksNAFlexible />} />
              <Route path="/volvo-rider" element={<VolvoRiderFlexible />} />
              <Route path="/polestar-engineered" element={<PolestarEngineeredFlexible />} />
              <Route path="/word-play" element={<WordPlayFlexible />} />
            </Routes>
            <ViewToggle />
            <SpeedInsights />
          </Router>
        </ThemeProvider>
      </HelmetProvider>
    </ErrorBoundary>
  </StrictMode>
);

inject();